import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    Button,
    Stack,
    Avatar,
    Divider,
    SvgIcon,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    LinearProgress
} from '@mui/material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import GitHubImportDialog from './GitHubImportDialog';

import AppsIcon from '@mui/icons-material/Apps';
import LaunchIcon from '@mui/icons-material/Launch';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import GitHubIcon from '@mui/icons-material/GitHub';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

const connectors = [
    {
        category: '🧩 Developer Integrations',
        items: [
            {
                name: 'GitHub',
                description:
                    'Keep branches up to date with localized strings by syncing pull requests directly with translation tasks.',
                status: 'Available',
                actionLabel: 'Connect',
            },
            {
                name: 'GitLab',
                description:
                    'Automate localization pipelines across your GitLab projects with built-in translation syncs.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Lokalise',
                description:
                    'Bridge your localization workflow by connecting Lokalise projects and translation updates in one place.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Crowdin',
                description:
                    'Streamline localization handoffs with Crowdin synchronization for tasks, strings, and statuses.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Smartling',
                description:
                    'Push content to Smartling workflows and pull approved translations back automatically.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Figma',
                description:
                    'Preview localized copy inside your Figma files without disrupting design iterations. Requires Plugin.',
                status: 'Available',
                actionLabel: 'Generate Key',
            },
            {
                name: 'Sketch',
                description:
                    'Design and review multilingual UI content with a direct Sketch integration.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'VS Code Extension',
                description:
                    'Manage translation strings from your editor with automatic suggestions and validations.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
        ],
    },
    {
        category: '💬 Communication & Collaboration',
        items: [
            {
                name: 'Slack',
                description:
                    'Trigger translation requests from channels and keep teams informed with instant notifications.',
                status: 'Available',
                actionLabel: 'Connect',
            },
            {
                name: 'Microsoft Teams',
                description:
                    'Collaborate across regions by connecting translation updates to your Teams channels.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Discord',
                description:
                    'Coordinate community localization efforts by sharing updates directly in Discord.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Google Meet',
                description:
                    'Capture meeting notes and action items that instantly flow into localization workflows.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Zoom',
                description:
                    'Distribute translated meeting assets and agendas with a single click from Zoom.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
        ],
    },
    {
        category: '🗂️ Project Management Tools',
        items: [
            {
                name: 'Trello',
                description:
                    'Convert Trello cards into translation tickets and surface progress without manual updates.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Asana',
                description:
                    'Sync tasks, due dates, and approvals between Asana and your localization projects.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'ClickUp',
                description:
                    'Automate ClickUp task creation for translation requests and surface completion updates instantly.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Monday.com',
                description:
                    'Keep localization efforts aligned with product roadmaps using Monday.com automations.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Jira',
                description:
                    'Create localization issues straight from Jira and track translations alongside development tasks.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
        ],
    },
    {
        category: '📁 File Storage & Document Management',
        items: [
            {
                name: 'Google Drive',
                description:
                    'Localize documents from shared drives with automatic versioning and delivery.',
                status: 'Available',
                actionLabel: 'Connect',
            },
            {
                name: 'Dropbox',
                description:
                    'Sync folders for translation and return finalized assets right where teams collaborate.',
                status: 'Available',
                actionLabel: 'Connect',
            },
            {
                name: 'OneDrive',
                description:
                    'Manage translation-ready files stored in OneDrive with automated workflows.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'SharePoint',
                description:
                    'Empower global teams with SharePoint content that stays localized and in sync.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'AWS S3',
                description:
                    'Automate ingestion and delivery of localized assets stored in your S3 buckets.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
        ],
    },
    {
        category: '✍️ Blog & Content Management',
        items: [
            {
                name: 'WordPress',
                description:
                    'Publish multilingual posts with seamless WordPress syncing for drafts and updates.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Ghost',
                description:
                    'Deliver consistent global content by syncing Ghost stories with translation workflows.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Webflow',
                description:
                    'Automate localized site launches by connecting Webflow collections to translation projects.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Notion',
                description:
                    'Translate shared Notion documentation and databases while keeping structure intact.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
            {
                name: 'Medium',
                description:
                    'Repurpose and localize Medium stories for every market with automated publishing.',
                status: 'Coming Soon',
                actionLabel: 'Coming Soon',
            },
        ],
    },
];

const createLogo = (children, viewBox = '0 0 64 64') => (
    <SvgIcon viewBox={viewBox} sx={{ fontSize: 32 }}>
        {children}
    </SvgIcon>
);

const renderLogo = name => {
    switch (name) {
        case 'GitHub':
            return <GitHubIcon sx={{ fontSize: 30, color: '#181717' }} />;
        case 'GitLab':
            return createLogo(
                <>
                    <path fill="#FC6D26" d="M32 60 6 24h12l14 36z" />
                    <path fill="#FCA326" d="M18 24 24 6l8 18H18z" />
                    <path fill="#FCA326" d="M46 24l-6-18-8 18h14z" />
                    <path fill="#E24329" d="M32 60 58 24h-12L32 60z" />
                </>,
            );
        case 'Lokalise':
            return createLogo(
                <>
                    <rect x="6" y="6" width="28" height="28" rx="8" fill="#FF6B2B" />
                    <rect x="30" y="30" width="28" height="28" rx="8" fill="#21C1A6" />
                    <path
                        fill="#fff"
                        d="M20 18h8v4h-4v8h-4v-12zm18 22h8v4h-12v-12h4v8z"
                    />
                </>,
            );
        case 'Crowdin':
            return createLogo(
                <>
                    <path
                        fill="#0052CC"
                        d="M32 6 6 44h20l6 14 6-14h20L32 6z"
                    />
                    <path fill="#36B37E" d="M32 22 20 40h8l4 8 4-8h8L32 22z" />
                </>,
            );
        case 'Smartling':
            return createLogo(
                <>
                    <circle cx="32" cy="32" r="26" fill="#FF4F5E" />
                    <path
                        fill="#fff"
                        d="M41 18c-5-1.5-9.5-1.1-12.5.9-4 2.7-3.5 7.8 1.5 9.7l6 2.3c2 .8 2.6 2.5 1.2 3.8-1.5 1.5-4.4 1.8-7.3.7-2-.7-3.8-1.8-5.3-3.2l-3.2 4.3c2 2 4.6 3.5 7.6 4.3 5.1 1.5 10.3.7 13.5-2.5 3.3-3.2 2.5-7.8-2-9.6l-6-2.3c-2.3-.9-2.7-2.6-1.2-3.8 1.3-1 3.6-1.3 6-.6 1.8.5 3.5 1.4 4.9 2.6l3-4.4C45.2 19.9 43.2 18.7 41 18z"
                    />
                </>,
            );
        case 'Figma':
            return createLogo(
                <>
                    <circle cx="24" cy="18" r="10" fill="#F24E1E" />
                    <circle cx="40" cy="18" r="10" fill="#FF7262" />
                    <circle cx="24" cy="34" r="10" fill="#A259FF" />
                    <circle cx="40" cy="34" r="10" fill="#1ABCFE" />
                    <circle cx="24" cy="50" r="10" fill="#0ACF83" />
                </>,
            );
        case 'Sketch':
            return createLogo(
                <>
                    <polygon fill="#FDB300" points="32 6 12 22 32 58 52 22" />
                    <polygon fill="#F4C417" points="32 6 20 22 44 22" />
                    <polygon fill="#EA6C00" points="12 22 32 58 20 22" />
                    <polygon fill="#EA6C00" points="44 22 32 58 52 22" />
                </>,
            );
        case 'VS Code Extension':
            return createLogo(
                <>
                    <path
                        fill="#0066B8"
                        d="M46 12 26 24l-8-6-8 8 8 8-8 8 8 8 8-6 20 12c2 1 4 0 4-2V14c0-2-2-3-4-2z"
                    />
                    <path fill="#1F9CF0" d="m46 12-20 12v16l20 12V12z" />
                </>,
            );
        case 'Slack':
            return createLogo(
                <>
                    <rect x="10" y="22" width="12" height="20" rx="6" fill="#36C5F0" />
                    <rect x="18" y="32" width="20" height="12" rx="6" fill="#2EB67D" />
                    <rect x="22" y="10" width="12" height="20" rx="6" fill="#E01E5A" />
                    <rect x="32" y="18" width="20" height="12" rx="6" fill="#ECB22E" />
                </>,
            );
        case 'Microsoft Teams':
            return createLogo(
                <>
                    <rect x="6" y="20" width="18" height="24" rx="4" fill="#464EB8" />
                    <rect x="22" y="16" width="26" height="32" rx="6" fill="#7B83EB" />
                    <circle cx="50" cy="22" r="6" fill="#505AC9" />
                    <path fill="#fff" d="M12 28h8v4h-2v8h-4v-8h-2v-4z" />
                </>,
            );
        case 'Discord':
            return createLogo(
                <>
                    <path
                        fill="#5865F2"
                        d="M16 18c6-4 26-4 32 0 4 6 6 12 6 20-6 6-12 8-22 8s-16-2-22-8c0-8 2-14 6-20z"
                    />
                    <path
                        fill="#fff"
                        d="M24 32c0 2-1.4 3.6-3 3.6S18 34 18 32s1.4-3.6 3-3.6 3 1.6 3 3.6zm22 0c0 2-1.4 3.6-3 3.6s-3-1.6-3-3.6 1.4-3.6 3-3.6 3 1.6 3 3.6z"
                    />
                </>,
            );
        case 'Google Meet':
            return createLogo(
                <>
                    <path fill="#00897B" d="M14 18h18v28H14a6 6 0 0 1-6-6V24a6 6 0 0 1 6-6z" />
                    <path fill="#FFB300" d="m32 18 18 14-18 14V18z" />
                    <path fill="#0F9D58" d="M14 18h18l-8 10H8v-4a6 6 0 0 1 6-6z" />
                    <path fill="#DB4437" d="M14 46h18l-8-10H8v4a6 6 0 0 0 6 6z" />
                    <path fill="#4285F4" d="m50 32-18-14v12l8 2-8 2v12l18-14z" />
                </>,
            );
        case 'Zoom':
            return createLogo(
                <>
                    <rect x="10" y="18" width="44" height="28" rx="12" fill="#2D8CFF" />
                    <rect x="26" y="24" width="14" height="16" rx="4" fill="#fff" />
                    <path fill="#fff" d="M40 24v16l12-8-12-8z" />
                </>,
            );
        case 'Trello':
            return createLogo(
                <>
                    <rect x="10" y="14" width="44" height="36" rx="10" fill="#0052CC" />
                    <rect x="18" y="20" width="10" height="18" rx="3" fill="#fff" />
                    <rect x="34" y="20" width="10" height="26" rx="3" fill="#fff" />
                </>,
            );
        case 'Asana':
            return createLogo(
                <>
                    <circle cx="32" cy="44" r="10" fill="#F06A6A" />
                    <circle cx="22" cy="24" r="10" fill="#FAD06C" />
                    <circle cx="42" cy="24" r="10" fill="#61C3A1" />
                </>,
            );
        case 'ClickUp':
            return createLogo(
                <>
                    <path
                        fill="#7B68EE"
                        d="M32 8c-12 0-22 10-22 22h8a14 14 0 0 1 28 0h8c0-12-10-22-22-22z"
                    />
                    <path
                        fill="#FF4F8B"
                        d="M20 38c4 4 8 6 12 6s8-2 12-6l6 6c-6 6-12 10-18 10s-12-4-18-10l6-6z"
                    />
                </>,
            );
        case 'Monday.com':
            return createLogo(
                <>
                    <circle cx="20" cy="36" r="8" fill="#F05032" />
                    <circle cx="32" cy="28" r="8" fill="#F9C846" />
                    <circle cx="44" cy="20" r="8" fill="#50C878" />
                </>,
            );
        case 'Jira':
            return createLogo(
                <>
                    <path fill="#2684FF" d="M18 14h16l12 18-16 18H14l20-18-16-18z" />
                    <path fill="#0052CC" d="M34 14h12l-12 12-8-12h8z" />
                </>,
            );
        case 'Google Drive':
            return (
                <Box
                    component="img"
                    src="/assets/logo/googledrive-icon.png"
                    alt="Google Drive"
                    sx={{ width: 32, height: 32, objectFit: 'contain' }}
                />
            );
        case 'Dropbox':
            return (
                <Box
                    component="img"
                    src="/assets/logo/dropbox-logo.png"
                    alt="Dropbox"
                    sx={{ width: 32, height: 32, objectFit: 'contain' }}
                />
            );
        case 'OneDrive':
            return createLogo(
                <>
                    <path
                        fill="#094AB2"
                        d="M18 38a14 12 0 0 1 26-8 12 10 0 0 1 8 9c6 1 10 6 10 12H8c0-7 5-12 10-13z"
                    />
                    <path fill="#2D7FF9" d="M18 38a14 12 0 0 1 26-8 16 14 0 0 0-26 8z" />
                </>,
            );
        case 'SharePoint':
            return createLogo(
                <>
                    <circle cx="22" cy="30" r="14" fill="#0078D4" />
                    <circle cx="42" cy="38" r="14" fill="#2B88D8" />
                    <circle cx="42" cy="22" r="10" fill="#50E6FF" />
                    <path fill="#fff" d="M20 24h8v6h-5v4h5v6h-8v-16z" />
                </>,
            );
        case 'AWS S3':
            return createLogo(
                <>
                    <polygon fill="#F90" points="32 8 54 20 54 44 32 56 10 44 10 20" />
                    <polygon fill="#FFAC31" points="32 14 48 22 48 42 32 50 16 42 16 22" />
                    <path fill="#fff" d="M24 24h16v4h-6v16h-4V28h-6v-4z" />
                </>,
            );
        case 'WordPress':
            return createLogo(
                <>
                    <circle cx="32" cy="32" r="26" fill="#21759B" />
                    <path
                        fill="#fff"
                        d="M32 16a16 16 0 1 0 0 32 16 16 0 0 0 0-32zm-6 10h4l2 10 2-10h4l-4 16h-4l-4-16z"
                    />
                </>,
            );
        case 'Ghost':
            return createLogo(
                <>
                    <rect x="10" y="20" width="44" height="8" rx="4" fill="#9F9FA1" />
                    <rect x="16" y="32" width="32" height="8" rx="4" fill="#C6C6C8" />
                    <rect x="22" y="44" width="20" height="8" rx="4" fill="#E7E7E8" />
                </>,
            );
        case 'Webflow':
            return createLogo(
                <>
                    <path
                        fill="#4353FF"
                        d="M12 20c6 0 10 6 13 18 3-12 7-18 13-18 8 0 12 8 14 24h-8c-1-10-3-16-6-16-3 0-5 6-7 16h-8c-2-10-4-16-7-16-3 0-5 6-6 16H6c2-16 6-24 12-24z"
                    />
                </>,
            );
        case 'Notion':
            return createLogo(
                <>
                    <rect x="10" y="14" width="44" height="36" rx="6" fill="#111" />
                    <rect x="14" y="18" width="36" height="28" rx="4" fill="#fff" />
                    <path fill="#111" d="M24 42V24h4l10 12V24h4v18h-4l-10-12v12h-4z" />
                </>,
            );
        case 'Medium':
            return createLogo(
                <>
                    <rect x="10" y="18" width="44" height="28" rx="6" fill="#12100E" />
                    <path
                        fill="#fff"
                        d="M22 24h4l6 12 6-12h4v16h-4V30l-6 12-6-12v10h-4V24z"
                    />
                </>,
            );
        default:
            return <BuildCircleIcon sx={{ fontSize: 28 }} />;
    }
};

const statusColor = status => {
    switch (status) {
        case 'Available':
            return 'success';
        case 'Beta':
            return 'warning';
        case 'Coming Soon':
            return 'info';
        default:
            return 'default';
    }
};

const AppsMarketplace = () => {
    const token = useSelector((state) => state.auth.token);
    const [integrationStatus, setIntegrationStatus] = useState({
        github: false,
        slack: false,
        google: false,
        dropbox: false,
        details: { github: null, slack: null }
    });
    const [figmaKey, setFigmaKey] = useState(null);
    const [usage, setUsage] = useState({ count: 0, limit: 5, planName: '' });
    const [paymentModal, setPaymentModal] = useState({ open: false, appName: '', priceId: '', limit: 0 });
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const location = useLocation();
    const navigate = useNavigate();

    const getConnectorName = (appName) => {
        const map = {
            'github': 'GitHub',
            'slack': 'Slack',
            'google-drive': 'Google Drive',
            'dropbox': 'Dropbox',
            'figma': 'Figma' // though figma logic handles key generation differently
        };
        return map[appName] || null;
    };

    // Check for "connected" query param (redirect from backend)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const status = params.get('status');
        const app = params.get('app');

        // Handle Payment Return
        if (status === 'success' && app) {
            const connectorName = getConnectorName(app);
            const sessionId = params.get('session_id');

            const verifyAndConnect = async () => {
                if (sessionId) {
                    try {
                        // Force a sync by calling the verification endpoint
                        await axios.get(`/api/billing/checkout-session/${sessionId}`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                    } catch (err) {
                        console.error('Session verification failed', err);
                    }
                }

                if (connectorName) {
                    setSnackbar({ open: true, message: `${connectorName} subscription active! Connecting...`, severity: 'success' });
                    // Clean URL
                    navigate('/dashboard/apps', { replace: true });
                    // Auto-trigger connection
                    handleConnect(connectorName);
                }
            };

            verifyAndConnect();
        }
        // Legacy/Direct connection handling
        else if (status === 'slack_connected') {
            setSnackbar({ open: true, message: 'Slack connected successfully!', severity: 'success' });
            navigate('/dashboard/apps', { replace: true });
        } else if (params.get('github') === 'connected') {
            setSnackbar({ open: true, message: 'GitHub connected successfully!', severity: 'success' });
            navigate('/dashboard/apps', { replace: true });
        } else if (params.get('google') === 'connected') {
            setSnackbar({ open: true, message: 'Google Drive connected successfully!', severity: 'success' });
            navigate('/dashboard/apps', { replace: true });
        } else if (params.get('dropbox') === 'connected') {
            setSnackbar({ open: true, message: 'Dropbox connected successfully!', severity: 'success' });
            navigate('/dashboard/apps', { replace: true });
        }
    }, [location.search, navigate]);

    // Check status on mount
    useEffect(() => {
        if (token) {
            fetchStatus();
            fetchFigmaKeys();
        }
    }, [token]);

    const fetchFigmaKeys = async () => {
        try {
            const res = await axios.get('/api/keys', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && res.data.length > 0) {
                // Determine if valid key exists
                const key = res.data.find(k => k.name.includes('Figma'));
                if (key) setFigmaKey(key);
            }
        } catch (err) {
            console.error('Failed to fetch keys', err);
        }
    };

    const generateFigmaKey = async () => {
        try {
            await axios.post('/api/keys', { name: 'Figma Plugin Key' }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Re-fetch to check limit
            // Actually post might fail if limit reached?
            // Need to update apiKeyRoutes POST to return 402 too if implemented there.
            // But let's assume we handle it.
            // If post returns 402, catch it.
            fetchFigmaKeys();
            setSnackbar({ open: true, message: 'API Key Generated!', severity: 'success' });
        } catch (err) {
            if (err.response && err.response.status === 402) {
                setPaymentModal({
                    open: true,
                    appName: 'figma',
                    priceId: err.response.data.priceId,
                    limit: err.response.data.limit
                });
            } else {
                setSnackbar({ open: true, message: 'Failed to generate key', severity: 'error' });
            }
        }
    };

    const copyKey = () => {
        if (figmaKey && figmaKey.key) {
            navigator.clipboard.writeText(figmaKey.key);
            setSnackbar({ open: true, message: 'Key copied to clipboard', severity: 'success' });
        }
    };

    const fetchStatus = async () => {
        try {
            const res = await axios.get('/api/integrations/status', {
                headers: { Authorization: `Bearer ${token}` }
            });
            // If backend returns { github: bool, slack: bool, details... }
            // Ensure state shape matches
            setIntegrationStatus(res.data);
            if (res.data.usage) {
                setUsage(res.data.usage);
            }
        } catch (err) {
            console.error(err);
            // Fallback for legacy backend if needed, but we just updated backend
        }
    };

    const handleSubscribe = async () => {
        try {
            const res = await axios.post('/api/billing/create-app-subscription', {
                appName: paymentModal.appName,
                returnUrl: window.location.href
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.status === 'checkout_required') {
                window.location.href = res.data.url;
            } else {
                setPaymentModal({ ...paymentModal, open: false });
                setSnackbar({ open: true, message: 'Subscription active! Connecting...', severity: 'success' });
                fetchStatus();

                // Auto-connect immediately if no checkout was needed
                const connectorName = getConnectorName(paymentModal.appName);
                if (connectorName) {
                    handleConnect(connectorName);
                }
            }
        } catch (err) {
            console.error('Subscription Error:', err);
            setSnackbar({ open: true, message: 'Failed to create subscription', severity: 'error' });
        }
    };

    const handleConnect = async (connectorName) => {
        try {
            if (connectorName === 'GitHub') {
                const res = await axios.get('/api/integrations/github/auth', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { token } // Pass token to be used as state
                });
                window.location.href = res.data.url;
            } else if (connectorName === 'Slack') {
                const res = await axios.get('/api/integrations/slack/auth', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { token }
                });
                window.location.href = res.data.url;
            } else if (connectorName === 'Google Drive') {
                const res = await axios.get('/api/integrations/google/auth', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                window.location.href = res.data.url;
            } else if (connectorName === 'Dropbox') {
                const res = await axios.get('/api/dropbox/auth', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                window.location.href = res.data.url;
            }
        } catch (err) {
            if (err.response && err.response.status === 402) {
                const { priceId, limit, appName } = err.response.data;
                // appName from backend might be 'github', 'slack', etc.
                // We store it for the subscribe call
                setPaymentModal({
                    open: true,
                    appName: appName || connectorName.toLowerCase().replace(' ', '-'),
                    priceId,
                    limit
                });
            } else {
                setSnackbar({ open: true, message: `Failed to initiate connection`, severity: 'error' });
            }
        }
    };

    const [disconnectDialog, setDisconnectDialog] = useState({ open: false, appName: '' });

    const openDisconnectDialog = (appName) => {
        setDisconnectDialog({ open: true, appName });
    };

    const confirmDisconnect = async () => {
        const connectorName = disconnectDialog.appName;
        setDisconnectDialog({ ...disconnectDialog, open: false });

        if (connectorName === 'GitHub') {
            try {
                await axios.delete('/api/integrations/github', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchStatus(); // Refresh
                setSnackbar({ open: true, message: 'GitHub disconnected', severity: 'info' });
            } catch (err) {
                setSnackbar({ open: true, message: 'Failed to disconnect', severity: 'error' });
            }
        } else if (connectorName === 'Google Drive') {
            try {
                await axios.post('/api/integrations/google/disconnect', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchStatus(); // Refresh
                setSnackbar({ open: true, message: 'Google Drive disconnected', severity: 'info' });
            } catch (err) {
                setSnackbar({ open: true, message: 'Failed to disconnect', severity: 'error' });
            }
        } else if (connectorName === 'Dropbox') {
            try {
                await axios.post('/api/dropbox/disconnect', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchStatus(); // Refresh
                setSnackbar({ open: true, message: 'Dropbox disconnected', severity: 'info' });
            } catch (err) {
                setSnackbar({ open: true, message: 'Failed to disconnect', severity: 'error' });
            }
        } else if (connectorName === 'Slack') {
            try {
                await axios.delete('/api/integrations/slack', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                fetchStatus(); // Refresh
                setSnackbar({ open: true, message: 'Slack disconnected', severity: 'info' });
            } catch (err) {
                setSnackbar({ open: true, message: 'Failed to disconnect', severity: 'error' });
            }
        }
    };

    const handleDisconnect = (connectorName) => {
        openDisconnectDialog(connectorName);
    };

    const handleSwitchAccount = async (connectorName) => {
        try {
            // Local-only disconnect (keep subscription)
            if (connectorName === 'GitHub') {
                await axios.delete('/api/integrations/github', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { keepSubscription: 'true' }
                });
            } else if (connectorName === 'Slack') {
                await axios.delete('/api/integrations/slack', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { keepSubscription: 'true' }
                });
            } else if (connectorName === 'Google Drive') {
                await axios.post('/api/integrations/google/disconnect?keepSubscription=true', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else if (connectorName === 'Dropbox') {
                await axios.post('/api/dropbox/disconnect?keepSubscription=true', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            // Re-trigger connect flow immediately
            handleConnect(connectorName);
        } catch (err) {
            console.error('Switch Account Error:', err);
            setSnackbar({ open: true, message: 'Failed to initiate switch', severity: 'error' });
        }
    };

    const renderAction = (connector) => {
        if (connector.name === 'GitHub') {
            const isConnected = integrationStatus.github; // New state structure
            if (isConnected) {
                return (
                    <Stack spacing={1}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={() => setImportDialogOpen(true)}
                        >
                            Import from GitHub
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            fullWidth
                            onClick={() => handleSwitchAccount('GitHub')}
                        >
                            Switch Account
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            fullWidth
                            onClick={() => handleDisconnect('GitHub')}
                        >
                            Disconnect ({integrationStatus.details?.github?.username})
                        </Button>
                    </Stack>
                );

            } else {
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        endIcon={<LaunchIcon />}
                        onClick={() => handleConnect('GitHub')}
                    >
                        Connect GitHub
                    </Button>
                );
            }
        } else if (connector.name === 'Slack') {
            const isConnected = integrationStatus.slack;
            const details = integrationStatus.details?.slack || {};

            if (isConnected) {
                return (
                    <Stack spacing={1}>
                        <Button
                            variant="contained"
                            color="success"
                            disabled
                            fullWidth
                            startIcon={<AssignmentTurnedInIcon />}
                            sx={{ textTransform: 'none', color: '#fff' }}
                        >
                            Connected {details.slackTeamName ? `(${details.slackTeamName})` : ''}
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            fullWidth
                            onClick={() => handleSwitchAccount('Slack')}
                        >
                            Switch Account
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            fullWidth
                            onClick={() => handleDisconnect('Slack')}
                        >
                            Disconnect
                        </Button>
                    </Stack>
                );
            } else {
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        endIcon={<LaunchIcon />}
                        onClick={() => handleConnect('Slack')}
                    >
                        Connect Slack
                    </Button>
                );
            }
        } else if (connector.name === 'Google Drive') {
            const isConnected = integrationStatus.google;

            if (isConnected) {
                return (
                    <Stack spacing={1}>
                        <Button
                            variant="contained"
                            color="success"
                            disabled
                            fullWidth
                            startIcon={<AssignmentTurnedInIcon />}
                            sx={{ textTransform: 'none', color: '#fff' }}
                        >
                            Connected
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            fullWidth
                            onClick={() => handleSwitchAccount('Google Drive')}
                        >
                            Switch Account
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            fullWidth
                            onClick={() => handleDisconnect('Google Drive')}
                        >
                            Disconnect
                        </Button>
                    </Stack>
                );
            } else {
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        endIcon={<LaunchIcon />}
                        onClick={() => handleConnect('Google Drive')}
                    >
                        Connect Google Drive
                    </Button>
                );
            }
        } else if (connector.name === 'Dropbox') {
            const isConnected = integrationStatus.dropbox;
            if (isConnected) {
                return (
                    <Stack spacing={1}>
                        <Button
                            variant="contained"
                            color="success"
                            disabled
                            fullWidth
                            startIcon={<AssignmentTurnedInIcon />}
                            sx={{ textTransform: 'none', color: '#fff' }}
                        >
                            Connected
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            fullWidth
                            onClick={() => handleSwitchAccount('Dropbox')}
                        >
                            Switch Account
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            fullWidth
                            onClick={() => handleDisconnect('Dropbox')}
                        >
                            Disconnect
                        </Button>
                    </Stack>
                );
            } else {
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        endIcon={<LaunchIcon />}
                        onClick={() => handleConnect('Dropbox')}
                    >
                        Connect Dropbox
                    </Button>
                );
            }
        } else if (connector.name === 'Figma') {
            if (figmaKey) {
                return (
                    <Stack spacing={1}>
                        <Chip
                            label="Key Generated"
                            color="success"
                            size="small"
                            icon={<AssignmentTurnedInIcon />}
                        />
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 0.5, borderRadius: 1, textAlign: 'center' }}>
                            {figmaKey.key.substring(0, 8)}...
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={copyKey}
                        >
                            Copy Key
                        </Button>
                    </Stack>
                );
            } else {
                return (
                    <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        onClick={generateFigmaKey}
                        endIcon={<LaunchIcon />}
                    >
                        Generate Key
                    </Button>
                );
            }
        }

        // Default for others
        return (
            <Button
                variant="contained"
                color="primary"
                fullWidth
                endIcon={<LaunchIcon />}
                disabled={connector.status === 'Coming Soon'}
            >
                {connector.actionLabel}
            </Button>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
            <GitHubImportDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                token={token}
                onImportSuccess={(projectId) => {
                    setSnackbar({ open: true, message: 'Project imported successfully!', severity: 'success' });
                    // Optional: navigate to project
                    // navigate(`/projects/${projectId}`);
                }}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64 }}>
                    <AppsIcon fontSize="large" />
                </Avatar>
                <Box>
                    <Typography variant="h4" fontWeight={700} gutterBottom>
                        Apps Marketplace
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Discover connectors that bring translation requests in from the tools your teams already use.
                        Install an app to streamline handoffs, automate project creation, and keep everyone informed.
                    </Typography>
                </Box>
            </Stack>

            {/* App Usage Section */}
            <Card sx={{ mt: 4, mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">
                            App Usage
                        </Typography>
                        <Typography variant="body2" color={usage.count >= usage.limit ? 'error' : 'text.secondary'}>
                            {usage.limit === 999999 ? 'Unlimited' : `${usage.count} / ${usage.limit}`} Connected
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={usage.limit === 999999 ? 0 : Math.min((usage.count / usage.limit) * 100, 100)}
                        color={usage.count >= usage.limit ? 'error' : 'primary'}
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Plan: {usage.planName || 'Free'}. {usage.count >= usage.limit && 'Limit reached. Additional apps require a subscription.'}
                    </Typography>
                </CardContent>
            </Card>

            <Card sx={{ mt: 4, borderRadius: 3, backgroundColor: 'background.paper' }}>
                <CardContent>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        justifyContent="space-between"
                        spacing={2}
                    >
                        <Box>
                            <Typography variant="h6">Build your own integration</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Use our Translation API and webhooks to connect any system with custom workflows.
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            color="primary"
                            endIcon={<LaunchIcon />}
                            component="a"
                            href="/docs/api"
                        >
                            View API Docs
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            {connectors.map(section => (
                <Box key={section.category} sx={{ mt: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {section.category}
                    </Typography>
                    <Grid container spacing={3}>
                        {section.items.map(connector => (
                            <Grid item xs={12} sm={6} md={4} key={connector.name}>
                                <Card sx={{ height: '100%', borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                            <Avatar
                                                variant="rounded"
                                                sx={{
                                                    bgcolor: 'background.paper',
                                                    border: theme => `1px solid ${theme.palette.divider}`,
                                                    width: 56,
                                                    height: 56,
                                                }}
                                            >
                                                {renderLogo(connector.name)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h6">{connector.name}</Typography>
                                                <Chip
                                                    size="small"
                                                    color={statusColor(connector.status)}
                                                    label={connector.status}
                                                />
                                            </Box>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {connector.description}
                                        </Typography>
                                    </CardContent>
                                    <Divider />
                                    <Box sx={{ p: 2 }}>
                                        {renderAction(connector)}
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ))}
            <Dialog open={paymentModal.open} onClose={() => setPaymentModal({ ...paymentModal, open: false })}>
                <DialogTitle>Subscribe to {paymentModal.appName}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The "{paymentModal.appName}" integration requires a paid add-on subscription.
                    </DialogContentText>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #ddd' }}>
                        <Typography variant="h6" color="primary">
                            £{paymentModal.limit === 0 ? '5.00' : '5.00'} / month
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Adds {paymentModal.appName} connectivity to your plan.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentModal({ ...paymentModal, open: false })}>Cancel</Button>
                    <Button onClick={handleSubscribe} variant="contained" color="primary">
                        Subscribe & Connect
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={disconnectDialog.open}
                onClose={() => setDisconnectDialog({ ...disconnectDialog, open: false })}
            >
                <DialogTitle>Disconnect {disconnectDialog.appName}?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to disconnect <strong>{disconnectDialog.appName}</strong>?
                    </DialogContentText>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Disconnecting this app will also <strong>cancel your subscription</strong> for this integration at the end of the billing period.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDisconnectDialog({ ...disconnectDialog, open: false })}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={confirmDisconnect}>
                        Disconnect & Cancel Subscription
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AppsMarketplace;
